import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
// ⚠️ `Loader2` ET NON UN <svg> ÉCRIT À LA MAIN.
// Ce dialogue affichait son chargement avec un SVG recopié deux fois dans le
// fichier, ni `Loader2` ni même une icône Lucide — contre la règle « Icônes :
// Lucide React uniquement » du CLAUDE.md. Comme il est partagé par 45 écrans,
// la MÊME page pouvait montrer deux chargements différents : sur les élèves
// radiés, le chemin multi-sélection passait par ici, le chemin individuel par
// `Loader2`. Corrigé le 05/10/2026, sur retour de recette.
import { CheckCircle, AlertTriangle, HelpCircle, Info, X, ChevronDown, Search, Check, Loader2 } from 'lucide-react';
import { api } from '../services/api';
import { CONFIRM_DIALOG_OVERLAY_CLASS } from './ui/modalOverlay';

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmation",
  description = "Êtes-vous sûr de vouloir effectuer cette action ?",
  confirmText = "Confirmer",
  cancelText = "Annuler",
  type = "info", // info, success, warning, error, question
  customIcon = null,
  requireReason = false,
  isLoading = false, // ✅ AJOUTÉ
  confirmDisabled = false,
  children = null,
  contentClassName = '',
  // Facultatif : où rendre le focus à la fermeture (Radix `onCloseAutoFocus`).
  // Sans déclencheur Radix, le focus retombait sur <body> ; les portails
  // famille et enseignant le rendent à ce qui avait ouvert la confirmation
  // (src/espaces/ConfirmationEspace.jsx). Absent : rien ne change.
  onCloseAutoFocus = undefined
}) => {
  const [step, setStep] = useState("confirm");
  const [reasons, setReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState("");
  const [isReasonDropdownOpen, setIsReasonDropdownOpen] = useState(false);
  const [reasonSearch, setReasonSearch] = useState("");
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep("confirm");
      setSelectedReason("");
      setIsReasonDropdownOpen(false);
      setReasonSearch("");
      setIsLoadingReasons(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (requireReason && step === "reason") {
      const fetchReasons = async () => {
        try {
          setIsLoadingReasons(true);
          const data = await api.eleves.getRadiationReasons();
          setReasons(Array.isArray(data) ? data : []);
        } catch (error) {
          console.error("Erreur lors du chargement des raisons:", error);
          setReasons([]);
        } finally {
          setIsLoadingReasons(false);
        }
      };
      fetchReasons();
    }
  }, [requireReason, step]);

  useEffect(() => {
    if (step !== "reason") {
      setIsReasonDropdownOpen(false);
      setReasonSearch("");
    }
  }, [step]);

  const handleConfirmClick = async () => {
    if (requireReason) {
      // Étape 1 → passe à la sélection de raison
      setStep("reason");
      return;
    }

    // Comportement classique
    try {
      await onConfirm();
      // ✅ Ne pas fermer ici, c'est géré dans le parent
    } catch (error) {
      console.error('Erreur dans ConfirmDialog:', error);
    }
  };

  const handleReasonConfirm = async () => {
    if (!selectedReason) {
      alert("Veuillez sélectionner une raison avant de confirmer.");
      return;
    }

    try {
      await onConfirm(selectedReason);
      // ✅ Ne pas fermer ici, c'est géré dans le parent
    } catch (error) {
      console.error("Erreur dans ConfirmDialog (reason):", error);
    }
  };

  const handleOpenChange = (open) => {
    // Empêche la fermeture automatique par clic extérieur si en cours de traitement
    if (!open && !isLoading) {
      onClose();
    }
  };

  const normalizeText = (value = "") =>
    value
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const selectedReasonDetails = reasons.find((reason) => String(reason.id) === String(selectedReason));
  const filteredReasons = reasons.filter((reason) =>
    normalizeText(`${reason.libelle || ""} ${reason.description || ""}`).includes(normalizeText(reasonSearch))
  );

  const getIconAndColor = () => {
    if (customIcon) {
      return { icon: customIcon, color: "text-blue-600", bgColor: "bg-blue-50" };
    }

    switch (type) {
      case 'success':
        return { icon: <CheckCircle size={32} />, color: "text-green-600", bgColor: "bg-green-50" };
      case 'warning':
        return { icon: <AlertTriangle size={32} />, color: "text-yellow-600", bgColor: "bg-yellow-50" };
      case 'error':
        return { icon: <X size={32} />, color: "text-red-600", bgColor: "bg-red-50" };
      case 'question':
        return { icon: <HelpCircle size={32} />, color: "text-blue-600", bgColor: "bg-blue-50" };
      default:
        return { icon: <Info size={32} />, color: "text-blue-600", bgColor: "bg-blue-50" };
    }
  };

  const { icon, color, bgColor } = getIconAndColor();

  return (
    <Dialog 
  open={isOpen} 
  onOpenChange={(open) => {
    // Ne fermer QUE si on clique sur Annuler/Fermer
    // Ne PAS fermer au clic extérieur ou ESC
    if (!open && !isLoading) {
      // Ne rien faire - empêche la fermeture au clic extérieur
    }
  }}
>
      <DialogPortal>
       <DialogOverlay 
  className={CONFIRM_DIALOG_OVERLAY_CLASS} 
  onClick={(e) => e.preventDefault()}
/>
        <DialogContent 
  className={`sm:max-w-md z-[10000] ${contentClassName}`}
  onPointerDownOutside={(e) => e.preventDefault()}
  onInteractOutside={(e) => e.preventDefault()}
  onEscapeKeyDown={(e) => e.preventDefault()}
  onCloseAutoFocus={onCloseAutoFocus}
>
          <DialogHeader className="text-center">
            <div className={`mx-auto w-14 h-14 rounded-full ${bgColor} flex items-center justify-center mb-4`}>
              <div className={color}>{icon}</div>
            </div>

            {step === "confirm" ? (
              <>
                <DialogTitle className="text-xl">{title}</DialogTitle>
                <DialogDescription className="mt-2">{description}</DialogDescription>
              </>
            ) : (
              <>
                <DialogTitle className="text-xl">Raison de la radiation</DialogTitle>
                <DialogDescription className="mt-2">
                  Sélectionnez la raison de la radiation avant de confirmer.
                </DialogDescription>
              </>
            )}
          </DialogHeader>

          {/* Étape 2 : champ raison */}
          {step === "reason" && (
            <div className="mt-4 text-left">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Motif de radiation
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    if (!isLoading && reasons.length > 0) {
                      setIsReasonDropdownOpen((current) => !current);
                    }
                  }}
                  disabled={isLoading || isLoadingReasons || reasons.length === 0}
                  className={`flex h-11 w-full items-center justify-between rounded-lg border bg-white px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    selectedReason
                      ? "border-[#246990]/30 text-[#246990]"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  <span className="truncate">
                    {selectedReasonDetails?.libelle || (isLoadingReasons ? "Chargement des motifs..." : reasons.length > 0 ? "Choisir une raison" : "Aucun motif disponible")}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`ml-2 shrink-0 transition-transform ${isReasonDropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isReasonDropdownOpen && !isLoading && (
                  <div className="absolute left-0 right-0 z-[10001] mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl">
                    <div className="border-b border-gray-100 bg-gray-50 p-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="text"
                          value={reasonSearch}
                          onChange={(event) => setReasonSearch(event.target.value)}
                          placeholder="Rechercher une raison..."
                          className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="max-h-56 overflow-y-auto py-1">
                      {filteredReasons.length === 0 ? (
                        <div className="px-3 py-3 text-center text-sm text-gray-500">Aucun motif trouvé</div>
                      ) : (
                        filteredReasons.map((reason) => {
                          const isSelected = String(reason.id) === String(selectedReason);

                          return (
                            <button
                              key={reason.id}
                              type="button"
                              onClick={() => {
                                setSelectedReason(String(reason.id));
                                setIsReasonDropdownOpen(false);
                                setReasonSearch("");
                              }}
                              className={`flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                                isSelected
                                  ? "bg-[#246990]/10 font-semibold text-[#246990]"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              <span className="min-w-0">
                                <span className="block truncate">{reason.libelle}</span>
                                {reason.description && (
                                  <span className="mt-0.5 block text-xs font-normal text-gray-500">{reason.description}</span>
                                )}
                              </span>
                              {isSelected && <Check size={16} className="mt-0.5 shrink-0 text-[#246990]" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {selectedReasonDetails?.description && (
                <p className="mt-2 rounded-lg bg-[#246990]/5 px-3 py-2 text-xs leading-relaxed text-gray-600">
                  {selectedReasonDetails.description}
                </p>
              )}
            </div>
          )}

          {step === "confirm" && children && (
            <div className="mt-4 text-left">
              {children}
            </div>
          )}

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
            {step === "reason" ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setStep("confirm")}
                  disabled={isLoading} // ✅ Désactiver pendant le chargement
                  className="text-base px-5 py-2 h-auto"
                >
                  Retour
                </Button>
                <Button
                  onClick={handleReasonConfirm}
                  disabled={isLoading || !selectedReason} // ✅ Désactiver pendant le chargement
                  className="flex items-center gap-2 bg-[#246990] hover:bg-[#246990]/90 text-white text-base px-5 py-2 h-auto rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      Traitement...
                    </>
                  ) : (
                    'Confirmer la radiation'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isLoading} // ✅ Désactiver pendant le chargement
                  className="text-base px-5 py-2 h-auto"
                >
                  {cancelText}
                </Button>
                <Button
                  onClick={handleConfirmClick}
                  disabled={isLoading || confirmDisabled} // ✅ Désactiver pendant le chargement
                  className="flex items-center gap-2 bg-[#246990] hover:bg-[#246990]/90 text-white text-base px-5 py-2 h-auto rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4" />
                      Traitement...
                    </>
                  ) : (
                    confirmText
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export default ConfirmDialog;
